/**
 * PaymentsTab - Invoice creation and payment management
 * Extracted from old HomePage.js for HomePageNew.js integration
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import axios from "axios";
import { format, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  CreditCard, Receipt, DollarSign, Send, Trash2, RefreshCw, CheckCircle, 
  Archive, ArchiveRestore, Printer, Download, CheckSquare, Square, 
  Eye, EyeOff, FileText, CalendarRange, CalendarDays, ListChecks, AlertCircle
} from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentsTab = () => {
  const { user, organization } = useAuth();
  const { language } = useLanguage();
  
  // Invoice state
  const [invoices, setInvoices] = useState([]);
  const [archivedInvoices, setArchivedInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoiceView, setInvoiceView] = useState("all"); // "all", "paid", "unpaid"
  const [hideAmounts, setHideAmounts] = useState(false); // Privacy toggle for amounts
  const [invoiceDateRange, setInvoiceDateRange] = useState({
    from: new Date(),
    to: new Date()
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [activeDateField, setActiveDateField] = useState('from'); // Track which field is active: 'from' or 'to'
  const [newInvoice, setNewInvoice] = useState({
    customerName: "",
    customerEmail: "",
    ccEmail: "",
    customerPhone: "",
    textParent: false,
    amount: "",
    description: "",
    notes: "",
    zelleEmail: "",
    zellePhone: "",
    allowCreditCard: false,
    creditCardFeePercent: 3.5,
    creditCardDiscount: 0
  });
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  
  // Square status for credit card payments
  const [squareConnected, setSquareConnected] = useState(false);
  const [connectingSquare, setConnectingSquare] = useState(false);

  // Filter invoices by date range
  const filterByDateRange = (invList) => {
    if (!invoiceDateRange.from || !invoiceDateRange.to) return invList;
    return invList.filter(inv => {
      const invDate = new Date(inv.createdAt);
      const rangeStart = new Date(invoiceDateRange.from);
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date(invoiceDateRange.to);
      rangeEnd.setHours(23, 59, 59, 999);
      return isWithinInterval(invDate, { 
        start: rangeStart, 
        end: rangeEnd
      });
    });
  };

  // Computed invoice lists (filtered by date range)
  const dateFilteredInvoices = filterByDateRange(invoices);
  const paidInvoices = dateFilteredInvoices.filter(inv => inv.paid);
  const unpaidInvoices = dateFilteredInvoices.filter(inv => !inv.paid);
  const totalPaid = paidInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const totalOutstanding = unpaidInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  
  // Get filtered invoices based on current view
  const getFilteredInvoices = () => {
    if (showArchived) return filterByDateRange(archivedInvoices);
    if (invoiceView === "paid") return paidInvoices;
    if (invoiceView === "unpaid") return unpaidInvoices;
    return dateFilteredInvoices;
  };
  const filteredInvoices = getFilteredInvoices();

  useEffect(() => {
    fetchInvoices();
    fetchArchivedInvoices();
    fetchSquareStatus();
  }, []);

  // Clear selection when switching views
  useEffect(() => {
    setSelectedInvoices([]);
  }, [showArchived, invoiceView]);

  const fetchInvoices = async () => {
    setInvoiceLoading(true);
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
    setInvoiceLoading(false);
  };

  const fetchArchivedInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices/archived`);
      setArchivedInvoices(response.data.invoices || []);
    } catch (error) {
      console.error("Error fetching archived invoices:", error);
    }
  };

  // Check Square connection status
  const fetchSquareStatus = async () => {
    try {
      const response = await axios.get(`${API}/square/status`);
      setSquareConnected(response.data?.connected || false);
    } catch (error) {
      console.error("Error fetching Square status:", error);
      setSquareConnected(false);
    }
  };

  // Connect Square account
  const handleConnectSquare = async () => {
    setConnectingSquare(true);
    try {
      const response = await axios.get(`${API}/square/authorize`);
      if (response.data?.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error("Error connecting Square:", error);
      toast.error("Failed to connect Square account");
      setConnectingSquare(false);
    }
  };

  const isValidEmailFormat = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

  const requestSendInvoice = (e) => {
    e.preventDefault();
    if (!newInvoice.customerName || !newInvoice.customerEmail || !newInvoice.amount || !newInvoice.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!isValidEmailFormat(newInvoice.customerEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if ((newInvoice.ccEmail || "").trim() && !isValidEmailFormat(newInvoice.ccEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    
    // Require at least one payment method (Zelle OR Credit Card)
    if (!newInvoice.zelleEmail && !newInvoice.zellePhone && !newInvoice.allowCreditCard) {
      toast.error("Please enable at least one payment method (Zelle or Credit Card)");
      return;
    }
    setShowSendConfirm(true);
  };

  const handleCreateInvoice = async () => {
    setShowSendConfirm(false);
    setCreatingInvoice(true);
    try {
      const response = await axios.post(`${API}/invoices`, {
        ...newInvoice,
        amount: parseFloat(newInvoice.amount),
        creditCardFeePercent: newInvoice.allowCreditCard ? parseFloat(newInvoice.creditCardFeePercent) : 0,
        creditCardDiscount: newInvoice.allowCreditCard ? parseFloat(newInvoice.creditCardDiscount || 0) : 0
      });
      if (response.data.emailSent) {
        toast.success(`Invoice ${response.data.invoice.invoiceNumber} created and sent!`);
      } else {
        toast.warning(`Invoice ${response.data.invoice.invoiceNumber} created, but the email did not send. Click Resend to try again.`);
      }
      setNewInvoice({ 
        customerName: "", 
        customerEmail: "", 
        ccEmail: "",
        customerPhone: "",
        textParent: false,
        amount: "", 
        description: "", 
        notes: "", 
        zelleEmail: "", 
        zellePhone: "",
        allowCreditCard: false,
        creditCardFeePercent: 3.5,
        creditCardDiscount: 0
      });
      fetchInvoices();
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    }
    setCreatingInvoice(false);
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      const response = await axios.put(`${API}/invoices/${invoiceId}/mark-paid`);
      const { customerEmailSent, ownerEmailSent } = response.data || {};
      if (customerEmailSent && ownerEmailSent) {
        toast.success("Payment confirmed! Receipts sent to customer and owner.");
      } else if (!customerEmailSent && !ownerEmailSent) {
        toast.warning("Payment recorded, but the email notifications did not send.");
      } else if (!customerEmailSent) {
        toast.warning("Payment recorded, but the customer receipt email did not send.");
      } else {
        toast.warning("Payment recorded, but the owner notification did not send.");
      }
      fetchInvoices();
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
    }
  };

  const handleResendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/resend`);
      toast.success("Invoice resent to customer");
    } catch (error) {
      console.error("Error resending invoice:", error);
      toast.error("Failed to resend invoice");
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to permanently delete this invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      fetchInvoices();
      fetchArchivedInvoices();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    }
  };

  const handleArchiveInvoice = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/archive`);
      toast.success("Invoice moved to archive");
      fetchInvoices();
      fetchArchivedInvoices();
    } catch (error) {
      console.error("Error archiving invoice:", error);
      toast.error("Failed to archive invoice");
    }
  };

  const handleUnarchiveInvoice = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/unarchive`);
      toast.success("Invoice restored from archive");
      fetchInvoices();
      fetchArchivedInvoices();
    } catch (error) {
      console.error("Error restoring invoice:", error);
      toast.error("Failed to restore invoice");
    }
  };

  const handlePrintInvoice = (invoice) => {
    const orgName = organization?.name || user?.organization?.name || "Childcare Center";
    const zelleInfo = invoice.zelleEmail || invoice.zellePhone || "Contact provider";
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoiceNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 3px solid #F59E0B; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #92400E; margin: 0; font-size: 28px; }
          .header p { color: #666; margin: 5px 0; }
          .invoice-details { background: #FEF3C7; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
          .invoice-details h2 { color: #92400E; margin-top: 0; }
          .row { display: flex; justify-content: space-between; margin: 10px 0; }
          .label { font-weight: bold; color: #374151; }
          .value { color: #1F2937; }
          .amount { font-size: 28px; color: #059669; font-weight: bold; text-align: center; margin: 20px 0; }
          .payment-info { background: #DBEAFE; padding: 20px; border-radius: 8px; margin-top: 20px; }
          .payment-info h3 { color: #1E40AF; margin-top: 0; }
          .description { background: #F3F4F6; padding: 15px; border-radius: 8px; margin: 15px 0; }
          .footer { text-align: center; margin-top: 40px; color: #9CA3AF; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${orgName}</h1>
          <p>Invoice</p>
        </div>
        
        <div class="invoice-details">
          <h2>Invoice #${invoice.invoiceNumber}</h2>
          <div class="row"><span class="label">Date Sent:</span> <span class="value">${new Date(invoice.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
          <div class="row"><span class="label">Customer:</span> <span class="value">${invoice.customerName}</span></div>
          <div class="row"><span class="label">Email:</span> <span class="value">${invoice.customerEmail}</span></div>
          ${invoice.paid ? `<div class="row"><span class="label">Status:</span> <span class="value" style="color: #059669;">✓ PAID on ${invoice.paidDate}</span></div>` : '<div class="row"><span class="label">Status:</span> <span class="value" style="color: #D97706;">PENDING</span></div>'}
        </div>
        
        <div class="description">
          <strong>Description:</strong><br/>
          ${invoice.description}
          ${invoice.notes ? `<br/><br/><strong>Notes:</strong> ${invoice.notes}` : ''}
        </div>
        
        <div class="amount">Amount Due: $${invoice.amount.toFixed(2)}</div>
        
        ${!invoice.paid ? `
        <div class="payment-info">
          <h3>Payment via Zelle</h3>
          <p>Please send payment via Zelle to:</p>
          <p style="font-size: 18px; font-weight: bold; color: #1E40AF;">${zelleInfo}</p>
          <p style="color: #6B7280;">Include Invoice #${invoice.invoiceNumber} in the memo.</p>
        </div>
        ` : ''}
        
        <div class="footer">
          <p>Thank you for your business!</p>
          <p>${orgName}</p>
          <p>Generated on ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Export single invoice as PDF
  const handleExportInvoicePDF = async (invoice) => {
    const orgName = organization?.name || user?.organization?.name || "Childcare Center";
    const zelleInfo = invoice.zelleEmail || invoice.zellePhone || "Contact provider";
    
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    
    // Header with gradient-like background
    pdf.setFillColor(245, 158, 11); // Amber
    pdf.rect(0, 0, pageWidth, 35, 'F');
    
    // Organization name
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.text(orgName, pageWidth / 2, 15, { align: 'center' });
    pdf.setFontSize(14);
    pdf.text('INVOICE', pageWidth / 2, 25, { align: 'center' });
    
    let y = 50;
    
    // Invoice details box
    pdf.setFillColor(254, 243, 199); // Amber-100
    pdf.rect(15, y - 5, pageWidth - 30, 50, 'F');
    
    pdf.setTextColor(146, 64, 14); // Amber-800
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Invoice #${invoice.invoiceNumber}`, 20, y + 5);
    
    pdf.setTextColor(55, 65, 81); // Gray-700
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    y += 15;
    pdf.text(`Date Sent: ${new Date(invoice.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, y);
    y += 8;
    pdf.text(`Customer: ${invoice.customerName}`, 20, y);
    y += 8;
    pdf.text(`Email: ${invoice.customerEmail}`, 20, y);
    y += 8;
    
    if (invoice.paid) {
      pdf.setTextColor(5, 150, 105); // Green
      pdf.text(`Status: PAID on ${invoice.paidDate}`, 20, y);
    } else {
      pdf.setTextColor(217, 119, 6); // Amber
      pdf.text('Status: PENDING', 20, y);
    }
    
    y += 20;
    
    // Description box
    pdf.setFillColor(243, 244, 246); // Gray-100
    pdf.rect(15, y - 5, pageWidth - 30, 30, 'F');
    pdf.setTextColor(55, 65, 81);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Description:', 20, y + 5);
    pdf.setFont('helvetica', 'normal');
    const descLines = pdf.splitTextToSize(invoice.description, pageWidth - 50);
    pdf.text(descLines, 20, y + 13);
    
    if (invoice.notes) {
      y += 20;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Notes:', 20, y);
      pdf.setFont('helvetica', 'normal');
      const notesLines = pdf.splitTextToSize(invoice.notes, pageWidth - 50);
      pdf.text(notesLines, 20, y + 8);
    }
    
    y += 35;
    
    // Amount - large and centered
    pdf.setFillColor(209, 250, 229); // Green-100
    pdf.rect(15, y - 5, pageWidth - 30, 25, 'F');
    pdf.setTextColor(5, 150, 105); // Green
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Amount Due: $${invoice.amount.toFixed(2)}`, pageWidth / 2, y + 10, { align: 'center' });
    
    y += 35;
    
    // Payment info (if not paid)
    if (!invoice.paid) {
      pdf.setFillColor(219, 234, 254); // Blue-100
      pdf.rect(15, y - 5, pageWidth - 30, 40, 'F');
      
      pdf.setTextColor(30, 64, 175); // Blue-800
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Payment via Zelle', 20, y + 5);
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Please send payment via Zelle to:', 20, y + 15);
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(zelleInfo, 20, y + 25);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128); // Gray-500
      pdf.text(`Include Invoice #${invoice.invoiceNumber} in the memo.`, 20, y + 33);
    }
    
    // Footer
    pdf.setTextColor(156, 163, 175); // Gray-400
    pdf.setFontSize(10);
    pdf.text('Thank you for your business!', pageWidth / 2, 270, { align: 'center' });
    pdf.text(orgName, pageWidth / 2, 277, { align: 'center' });
    pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, 284, { align: 'center' });
    
    pdf.save(`Invoice_${invoice.invoiceNumber}.pdf`);
    toast.success('Invoice PDF downloaded!');
  };

  // Bulk selection handlers
  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const toggleSelectAll = () => {
    const currentList = getFilteredInvoices();
    if (selectedInvoices.length === currentList.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(currentList.map(inv => inv.id));
    }
  };

  const handleBulkArchive = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("Please select invoices to archive");
      return;
    }
    if (!window.confirm(`Archive ${selectedInvoices.length} invoice(s)?`)) return;
    
    try {
      const response = await axios.post(`${API}/invoices/bulk-archive`, {
        invoiceIds: selectedInvoices
      });
      toast.success(response.data.message);
      setSelectedInvoices([]);
      fetchInvoices();
      fetchArchivedInvoices();
    } catch (error) {
      console.error("Error bulk archiving:", error);
      toast.error("Failed to archive invoices");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("Please select invoices to delete");
      return;
    }
    if (!window.confirm(`Permanently delete ${selectedInvoices.length} invoice(s)? This cannot be undone.`)) return;
    
    try {
      const response = await axios.post(`${API}/invoices/bulk-delete`, {
        invoiceIds: selectedInvoices
      });
      toast.success(response.data.message);
      setSelectedInvoices([]);
      fetchInvoices();
      fetchArchivedInvoices();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Failed to delete invoices");
    }
  };

  const handleExportAll = async () => {
    try {
      const response = await axios.get(`${API}/invoices/export?include_archived=true`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoices_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoices exported successfully!");
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Failed to export invoices");
    }
  };

  const handleExportSelected = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("Please select invoices to export");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/invoices/export-selected`, {
        invoiceIds: selectedInvoices
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoices_selected_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${selectedInvoices.length} invoice(s)!`);
    } catch (error) {
      console.error("Error exporting selected:", error);
      toast.error("Failed to export invoices");
    }
  };

  // Print Invoice Summary Report (Paid or Unpaid)
  const handlePrintInvoiceReport = (reportType) => {
    const orgName = organization?.name || user?.organization?.name || "Childcare Center";
    const reportInvoices = reportType === "paid" ? paidInvoices : unpaidInvoices;
    const reportTitle = reportType === "paid" ? "PAID INVOICES REPORT" : "UNPAID INVOICES REPORT";
    const totalAmount = reportType === "paid" ? totalPaid : totalOutstanding;
    const statusLabel = reportType === "paid" ? "Total Amount Paid" : "Total Outstanding";
    const statusColor = reportType === "paid" ? "#059669" : "#D97706";
    const dateRangeText = `${format(invoiceDateRange.from, 'MMM d, yyyy')} - ${format(invoiceDateRange.to, 'MMM d, yyyy')}`;
    
    const tableRows = reportInvoices.map((inv, idx) => `
      <tr style="border-bottom: 1px solid #E5E7EB; ${idx % 2 === 1 ? 'background-color: #F9FAFB;' : ''}">
        <td style="padding: 12px; font-weight: 500;">${inv.invoiceNumber}</td>
        <td style="padding: 12px;">${inv.customerName}</td>
        <td style="padding: 12px;">${inv.customerEmail}</td>
        <td style="padding: 12px;">${inv.description}</td>
        <td style="padding: 12px; text-align: right; font-weight: 600;">$${inv.amount.toFixed(2)}</td>
        <td style="padding: 12px;">${new Date(inv.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
        ${reportType === "paid" ? `<td style="padding: 12px; color: #059669;">${inv.paidDate || '-'}</td>` : ''}
      </tr>
    `).join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportTitle}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; max-width: 1100px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 3px solid ${statusColor}; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #1F2937; margin: 0 0 10px 0; font-size: 24px; }
          .header h2 { color: ${statusColor}; margin: 0; font-size: 18px; }
          .header p { color: #6B7280; margin: 10px 0 0; }
          .date-range { background: #F3F4F6; padding: 10px 20px; border-radius: 6px; display: inline-block; margin-top: 10px; }
          .date-range span { font-weight: 600; color: #374151; }
          .summary { display: flex; justify-content: space-between; margin-bottom: 30px; padding: 20px; background: ${reportType === "paid" ? '#ECFDF5' : '#FEF3C7'}; border-radius: 8px; }
          .summary-item { text-align: center; }
          .summary-item .label { font-size: 12px; color: #6B7280; margin-bottom: 4px; }
          .summary-item .value { font-size: 24px; font-weight: bold; color: ${statusColor}; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: ${reportType === "paid" ? '#059669' : '#D97706'}; color: white; padding: 12px; text-align: left; font-size: 12px; }
          th:nth-child(5) { text-align: right; }
          .total-row { background: ${reportType === "paid" ? '#D1FAE5' : '#FEF3C7'}; font-weight: bold; }
          .total-row td { padding: 15px 12px; font-size: 16px; }
          .footer { text-align: center; color: #9CA3AF; font-size: 11px; margin-top: 30px; border-top: 1px solid #E5E7EB; padding-top: 20px; }
          @media print { body { padding: 15px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${orgName}</h1>
          <h2>${reportTitle}</h2>
          <div class="date-range">Period: <span>${dateRangeText}</span></div>
          <p>Generated on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        
        <div class="summary">
          <div class="summary-item">
            <div class="label">Total Invoices</div>
            <div class="value">${reportInvoices.length}</div>
          </div>
          <div class="summary-item">
            <div class="label">${statusLabel}</div>
            <div class="value">$${totalAmount.toFixed(2)}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Email</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Date Sent</th>
              ${reportType === "paid" ? '<th>Date Paid</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${tableRows}
            <tr class="total-row">
              <td colspan="${reportType === "paid" ? '4' : '4'}"><strong>${statusLabel}:</strong></td>
              <td style="text-align: right;"><strong>$${totalAmount.toFixed(2)}</strong></td>
              <td colspan="${reportType === "paid" ? '2' : '1'}"></td>
            </tr>
          </tbody>
        </table>
        
        <div class="footer">
          <p>${orgName} • Invoice Summary Report • ${reportType === "paid" ? "Paid" : "Outstanding"} Invoices</p>
          <p>Page 1 of 1</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Export Invoice Report as PDF
  const handleExportInvoiceReportPDF = (reportType) => {
    const orgName = organization?.name || user?.organization?.name || "Childcare Center";
    const reportInvoices = reportType === "paid" ? paidInvoices : unpaidInvoices;
    const reportTitle = reportType === "paid" ? "PAID INVOICES REPORT" : "UNPAID INVOICES REPORT";
    const totalAmount = reportType === "paid" ? totalPaid : totalOutstanding;
    const statusLabel = reportType === "paid" ? "Total Amount Paid" : "Total Outstanding";
    const dateRangeText = `${format(invoiceDateRange.from, 'MMM d, yyyy')} - ${format(invoiceDateRange.to, 'MMM d, yyyy')}`;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(orgName, pageWidth / 2, 20, { align: "center" });
    
    doc.setFontSize(14);
    if (reportType === "paid") {
      doc.setTextColor(5, 150, 105);
    } else {
      doc.setTextColor(217, 119, 6);
    }
    doc.text(reportTitle, pageWidth / 2, 30, { align: "center" });
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Period: ${dateRangeText}`, pageWidth / 2, 38, { align: "center" });
    doc.text(`Generated: ${format(new Date(), 'MMMM d, yyyy')}`, pageWidth / 2, 44, { align: "center" });
    
    // Summary box
    if (reportType === "paid") {
      doc.setFillColor(236, 253, 245);
    } else {
      doc.setFillColor(254, 243, 199);
    }
    doc.roundedRect(14, 50, pageWidth - 28, 20, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("Total Invoices:", 25, 60);
    doc.text(`${statusLabel}:`, pageWidth - 80, 60);
    
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    if (reportType === "paid") {
      doc.setTextColor(5, 150, 105);
    } else {
      doc.setTextColor(217, 119, 6);
    }
    doc.text(`${reportInvoices.length}`, 25, 67);
    doc.text(`$${totalAmount.toFixed(2)}`, pageWidth - 80, 67);
    
    // Table
    const headers = reportType === "paid" 
      ? [["Invoice #", "Customer", "Email", "Description", "Amount", "Sent", "Paid"]]
      : [["Invoice #", "Customer", "Email", "Description", "Amount", "Date Sent"]];
    
    const data = reportInvoices.map(inv => {
      const row = [
        inv.invoiceNumber,
        inv.customerName,
        inv.customerEmail,
        inv.description?.substring(0, 30) + (inv.description?.length > 30 ? '...' : ''),
        `$${inv.amount.toFixed(2)}`,
        format(new Date(inv.createdAt), 'MM/dd/yy')
      ];
      if (reportType === "paid") {
        row.push(inv.paidDate || '-');
      }
      return row;
    });
    
    // Add total row
    const totalRow = reportType === "paid"
      ? [`${statusLabel}:`, '', '', '', `$${totalAmount.toFixed(2)}`, '', '']
      : [`${statusLabel}:`, '', '', '', `$${totalAmount.toFixed(2)}`, ''];
    data.push(totalRow);
    
    doc.autoTable({
      head: headers,
      body: data,
      startY: 78,
      theme: 'striped',
      headStyles: {
        fillColor: reportType === "paid" ? [5, 150, 105] : [217, 119, 6],
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 8
      },
      columnStyles: {
        4: { halign: 'right' }
      },
      didParseCell: function(data) {
        // Style the total row
        if (data.row.index === reportInvoices.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = reportType === "paid" ? [209, 250, 229] : [254, 243, 199];
        }
      }
    });
    
    // Footer
    const finalY = doc.lastAutoTable.finalY || 200;
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`${orgName} • Invoice Summary Report • ${reportType === "paid" ? "Paid" : "Outstanding"} Invoices`, pageWidth / 2, finalY + 15, { align: "center" });
    
    // Save
    const fileName = `${reportType}_invoices_${format(invoiceDateRange.from, 'yyyy-MM-dd')}_to_${format(invoiceDateRange.to, 'yyyy-MM-dd')}.pdf`;
    doc.save(fileName);
    toast.success(`${reportType === "paid" ? "Paid" : "Unpaid"} invoices report exported as PDF!`);
  };

  // Generate Tax Report PDF
  const handleExportTaxReportPDF = () => {
    const orgName = organization?.name || user?.organization?.name || "Childcare Center";
    const orgAddress = organization?.address || "";
    const orgPhone = organization?.phone || "";
    const orgEmail = organization?.email || user?.email || "";
    const dateRangeText = `${format(invoiceDateRange.from, 'MMMM d, yyyy')} - ${format(invoiceDateRange.to, 'MMMM d, yyyy')}`;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Header with organization info
    doc.setFillColor(34, 51, 84); // Dark blue header
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(orgName, pageWidth / 2, 18, { align: "center" });
    
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.text("INCOME TAX REPORT", pageWidth / 2, 28, { align: "center" });
    
    doc.setFontSize(10);
    doc.text(`Report Period: ${dateRangeText}`, pageWidth / 2, 38, { align: "center" });
    
    // Organization details box
    doc.setFillColor(245, 247, 250);
    doc.roundedRect(14, 52, pageWidth - 28, 25, 3, 3, 'F');
    
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.setFont("helvetica", "normal");
    if (orgAddress) doc.text(`Address: ${orgAddress}`, 20, 62);
    if (orgPhone) doc.text(`Phone: ${orgPhone}`, 20, 69);
    if (orgEmail) doc.text(`Email: ${orgEmail}`, pageWidth / 2, 62);
    doc.text(`Report Generated: ${format(new Date(), 'MMMM d, yyyy h:mm a')}`, pageWidth / 2, 69);
    
    // Summary Section
    doc.setFillColor(236, 253, 245); // Light green
    doc.roundedRect(14, 84, (pageWidth - 32) / 2, 35, 3, 3, 'F');
    
    doc.setFillColor(254, 243, 199); // Light amber
    doc.roundedRect(pageWidth / 2 + 2, 84, (pageWidth - 32) / 2, 35, 3, 3, 'F');
    
    // Paid summary
    doc.setFontSize(10);
    doc.setTextColor(5, 150, 105);
    doc.setFont("helvetica", "bold");
    doc.text("TOTAL INCOME RECEIVED", 20, 94);
    doc.setFontSize(20);
    doc.text(`$${totalPaid.toFixed(2)}`, 20, 108);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`${paidInvoices.length} paid invoice(s)`, 20, 115);
    
    // Outstanding summary
    doc.setTextColor(217, 119, 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("OUTSTANDING RECEIVABLES", pageWidth / 2 + 8, 94);
    doc.setFontSize(20);
    doc.text(`$${totalOutstanding.toFixed(2)}`, pageWidth / 2 + 8, 108);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`${unpaidInvoices.length} unpaid invoice(s)`, pageWidth / 2 + 8, 115);
    
    // Grand Total
    doc.setFillColor(34, 51, 84);
    doc.roundedRect(14, 124, pageWidth - 28, 18, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("TOTAL INVOICED FOR PERIOD:", 20, 135);
    doc.text(`$${(totalPaid + totalOutstanding).toFixed(2)}`, pageWidth - 20, 135, { align: "right" });
    
    // Paid Invoices Table
    let startY = 150;
    
    if (paidInvoices.length > 0) {
      doc.setTextColor(5, 150, 105);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("PAID INVOICES (Income Received)", 14, startY);
      
      const paidData = paidInvoices.map(inv => [
        inv.invoiceNumber,
        inv.customerName,
        inv.description?.substring(0, 35) || '-',
        format(new Date(inv.createdAt), 'MM/dd/yyyy'),
        inv.paidDate || '-',
        `$${inv.amount.toFixed(2)}`
      ]);
      
      // Add subtotal row
      paidData.push(['', '', '', '', 'SUBTOTAL:', `$${totalPaid.toFixed(2)}`]);
      
      doc.autoTable({
        head: [['Invoice #', 'Customer', 'Description', 'Date Sent', 'Date Paid', 'Amount']],
        body: paidData,
        startY: startY + 5,
        theme: 'striped',
        headStyles: {
          fillColor: [5, 150, 105],
          fontSize: 8,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8
        },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 35 },
          2: { cellWidth: 50 },
          3: { cellWidth: 22 },
          4: { cellWidth: 22 },
          5: { cellWidth: 22, halign: 'right' }
        },
        didParseCell: function(data) {
          if (data.row.index === paidInvoices.length) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [209, 250, 229];
          }
        }
      });
      
      startY = doc.lastAutoTable.finalY + 10;
    }
    
    // Check if we need a new page for unpaid invoices
    if (startY > pageHeight - 80 && unpaidInvoices.length > 0) {
      doc.addPage();
      startY = 20;
    }
    
    // Unpaid Invoices Table
    if (unpaidInvoices.length > 0) {
      doc.setTextColor(217, 119, 6);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("OUTSTANDING INVOICES (Accounts Receivable)", 14, startY);
      
      const unpaidData = unpaidInvoices.map(inv => [
        inv.invoiceNumber,
        inv.customerName,
        inv.description?.substring(0, 35) || '-',
        format(new Date(inv.createdAt), 'MM/dd/yyyy'),
        `$${inv.amount.toFixed(2)}`
      ]);
      
      // Add subtotal row
      unpaidData.push(['', '', '', 'SUBTOTAL:', `$${totalOutstanding.toFixed(2)}`]);
      
      doc.autoTable({
        head: [['Invoice #', 'Customer', 'Description', 'Date Sent', 'Amount']],
        body: unpaidData,
        startY: startY + 5,
        theme: 'striped',
        headStyles: {
          fillColor: [217, 119, 6],
          fontSize: 8,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 40 },
          2: { cellWidth: 60 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25, halign: 'right' }
        },
        didParseCell: function(data) {
          if (data.row.index === unpaidInvoices.length) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [254, 243, 199];
          }
        }
      });
      
      startY = doc.lastAutoTable.finalY + 10;
    }
    
    // Footer disclaimer
    const footerY = Math.min(startY + 15, pageHeight - 25);
    doc.setDrawColor(200);
    doc.line(14, footerY, pageWidth - 14, footerY);
    
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.setFont("helvetica", "italic");
    doc.text("This report is generated for tax and accounting purposes. Please retain for your records.", pageWidth / 2, footerY + 8, { align: "center" });
    doc.text(`${orgName} • Tax Report • ${dateRangeText}`, pageWidth / 2, footerY + 14, { align: "center" });
    
    // Save
    const fileName = `Tax_Report_${format(invoiceDateRange.from, 'yyyy-MM-dd')}_to_${format(invoiceDateRange.to, 'yyyy-MM-dd')}.pdf`;
    doc.save(fileName);
    toast.success("Tax report exported as PDF!");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="payments-view">
      {/* Create Invoice Form */}
      <Card className="bg-white border border-line shadow-sm">
        <CardHeader className="border-b border-line pb-4">
          <CardTitle className="text-xl font-semibold text-ink-main flex items-center gap-2">
            <Receipt className="w-5 h-5 text-brand-highlight" />
            {language === 'es' ? 'Crear Factura' : 'Create Invoice'}
          </CardTitle>
          <p className="text-ink-muted text-sm">
            {language === 'es' 
              ? 'Crear y enviar factura al cliente por correo electrónico' 
              : 'Create and email invoice to customer'}
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Send / Resend confirmation dialogs */}
          <AlertDialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
            <AlertDialogContent data-testid="invoice-send-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Send Invoice?</AlertDialogTitle>
                <AlertDialogDescription>Are you sure you want to send this invoice?</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="invoice-send-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction className="bg-teal-600 hover:bg-teal-700" onClick={handleCreateInvoice} data-testid="invoice-send-confirm-btn">Yes, Send</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog open={!!resendTarget} onOpenChange={(o) => { if (!o) setResendTarget(null); }}>
            <AlertDialogContent data-testid="invoice-resend-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Resend Invoice?</AlertDialogTitle>
                <AlertDialogDescription>Are you sure you want to resend this invoice?</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="invoice-resend-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction className="bg-teal-600 hover:bg-teal-700" onClick={() => { const id = resendTarget; setResendTarget(null); handleResendInvoice(id); }} data-testid="invoice-resend-confirm-btn">Yes, Resend</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <form onSubmit={requestSendInvoice} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-ink-main">{language === 'es' ? 'Nombre del Cliente' : 'Customer Name'} *</Label>
                <Input
                  value={newInvoice.customerName}
                  onChange={(e) => setNewInvoice({...newInvoice, customerName: e.target.value})}
                  placeholder="John Smith"
                  className="border-line"
                  data-testid="invoice-customer-name"
                />
              </div>
              <div>
                <Label className="text-ink-main">{language === 'es' ? 'Correo del Cliente' : 'Customer Email'} *</Label>
                <Input
                  type="email"
                  value={newInvoice.customerEmail}
                  onChange={(e) => setNewInvoice({...newInvoice, customerEmail: e.target.value})}
                  placeholder="customer@email.com"
                  className="border-line"
                  data-testid="invoice-customer-email"
                />
              </div>
            </div>
            <div>
              <Label className="text-ink-main">{language === 'es' ? 'CC Destinatario Adicional (opcional)' : 'CC Additional Recipient (optional)'}</Label>
              <Input
                type="email"
                value={newInvoice.ccEmail}
                onChange={(e) => setNewInvoice({...newInvoice, ccEmail: e.target.value})}
                placeholder="additional@example.com"
                className="border-line"
                data-testid="invoice-cc-email"
              />
            </div>
            <div className="grid grid-cols-1 gap-2 rounded-lg border border-line bg-surface-subtle p-3">
              <Label className="text-ink-main">{language === 'es' ? 'Móvil del Padre (opcional)' : 'Parent Mobile (optional)'}</Label>
              <Input
                type="tel"
                value={newInvoice.customerPhone}
                onChange={(e) => setNewInvoice({...newInvoice, customerPhone: e.target.value, textParent: e.target.value.trim() ? true : false})}
                placeholder="4121234567"
                className="border-line"
                data-testid="invoice-customer-phone"
              />
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-main">{language === 'es' ? 'Enviar SMS al padre que la factura fue enviada' : 'Text parent that the invoice was emailed'}</span>
                <Switch
                  checked={newInvoice.textParent}
                  onCheckedChange={(checked) => setNewInvoice({...newInvoice, textParent: checked})}
                  data-testid="invoice-text-parent-toggle"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-ink-main">{language === 'es' ? 'Monto' : 'Amount'} ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoice.amount}
                  onChange={(e) => setNewInvoice({...newInvoice, amount: e.target.value})}
                  placeholder="0.00"
                  className="border-line"
                  data-testid="invoice-amount"
                />
              </div>
              <div>
                <Label className="text-ink-main">{language === 'es' ? 'Descripción' : 'Description'} *</Label>
                <Input
                  value={newInvoice.description}
                  onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})}
                  placeholder="Weekly childcare - Jan 2026"
                  className="border-line"
                  data-testid="invoice-description"
                />
              </div>
            </div>
            <div>
              <Label className="text-ink-main">{language === 'es' ? 'Notas (opcional)' : 'Notes (optional)'}</Label>
              <Textarea
                value={newInvoice.notes}
                onChange={(e) => setNewInvoice({...newInvoice, notes: e.target.value})}
                placeholder="Additional notes..."
                className="border-line"
                rows={2}
              />
            </div>
            
            {/* Zelle Payment Info */}
            <div className="bg-brand-secondary/10 border border-brand-secondary/30 rounded-xl p-4 space-y-3">
              <p className="text-brand-secondary font-semibold flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                {language === 'es' ? 'Información de Pago Zelle' : 'Zelle Payment Option'}
              </p>
              <p className="text-ink-muted text-sm">
                {language === 'es' 
                  ? 'Ingrese el email O teléfono donde el cliente enviará el pago por Zelle.' 
                  : 'Enter the email OR phone number where customer should send Zelle payment.'}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-secondary">{language === 'es' ? 'Email para Zelle' : 'Zelle Email'}</Label>
                  <Input
                    type="email"
                    value={newInvoice.zelleEmail}
                    onChange={(e) => setNewInvoice({...newInvoice, zelleEmail: e.target.value})}
                    placeholder="your-email@example.com"
                    className="border-brand-secondary/30 bg-white"
                    data-testid="invoice-zelle-email"
                  />
                </div>
                <div className="relative">
                  <div className="absolute -left-3 top-1/2 transform -translate-y-1/2 bg-brand-secondary/20 text-brand-secondary text-xs font-bold px-1 rounded">
                    OR
                  </div>
                  <Label className="text-brand-secondary">{language === 'es' ? 'Teléfono para Zelle' : 'Zelle Phone'}</Label>
                  <Input
                    type="tel"
                    value={newInvoice.zellePhone}
                    onChange={(e) => setNewInvoice({...newInvoice, zellePhone: e.target.value})}
                    placeholder="412-555-1234"
                    className="border-brand-secondary/30 bg-white"
                    data-testid="invoice-zelle-phone"
                  />
                </div>
              </div>
            </div>

            {/* Credit Card Payment Option */}
            <div className={`border rounded-xl p-4 space-y-3 ${newInvoice.allowCreditCard ? 'bg-brand-highlight/10 border-brand-highlight/30' : 'bg-surface-bg border-line'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className={`w-5 h-5 ${newInvoice.allowCreditCard ? 'text-brand-highlight' : 'text-ink-muted'}`} />
                  <span className={`font-semibold ${newInvoice.allowCreditCard ? 'text-brand-highlight' : 'text-ink-muted'}`}>
                    {language === 'es' ? 'Opción de Tarjeta de Crédito' : 'Credit Card Payment Option'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-ink-muted">
                    {newInvoice.allowCreditCard ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setNewInvoice({...newInvoice, allowCreditCard: !newInvoice.allowCreditCard})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      newInvoice.allowCreditCard ? 'bg-brand-highlight' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        newInvoice.allowCreditCard ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {!squareConnected && (
                <div className="bg-brand-accent/10 border border-brand-accent/30 p-3 rounded-lg space-y-2">
                  <p className="text-brand-accent text-sm">
                    ⚠️ Square account not connected. Connect to enable credit card payments.
                  </p>
                  <Button
                    type="button"
                    onClick={handleConnectSquare}
                    disabled={connectingSquare}
                    className="bg-brand-highlight hover:bg-brand-highlight/90 text-white text-sm"
                    data-testid="connect-square-payments-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {connectingSquare ? 'Connecting...' : 'Connect Square Account'}
                  </Button>
                  <p className="text-xs text-ink-muted">
                    Fees: 2.9% + $0.30 (Square) + $5 platform fee passed to parent
                  </p>
                </div>
              )}

              {newInvoice.allowCreditCard && squareConnected && (
                <div className="space-y-3 pt-2 border-t border-brand-highlight/30">
                  <p className="text-brand-highlight text-sm">
                    Parents can pay with Credit/Debit Card, Apple Pay, or Google Pay via Square.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-brand-highlight">Processing Fee (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={newInvoice.creditCardFeePercent}
                        onChange={(e) => setNewInvoice({...newInvoice, creditCardFeePercent: e.target.value})}
                        placeholder="3.5"
                        className="border-brand-highlight/30 bg-white"
                      />
                      <p className="text-xs text-ink-muted mt-1">Fee charged to parent for card payment</p>
                    </div>
                    <div>
                      <Label className="text-brand-highlight">Discount for Card Payment ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newInvoice.creditCardDiscount}
                        onChange={(e) => setNewInvoice({...newInvoice, creditCardDiscount: e.target.value})}
                        placeholder="0.00"
                        className="border-brand-highlight/30 bg-white"
                      />
                      <p className="text-xs text-ink-muted mt-1">Optional discount if paying by card</p>
                    </div>
                  </div>
                  {newInvoice.amount && (
                    <div className="bg-white p-3 rounded border border-brand-highlight/30">
                      <p className="text-sm font-medium text-brand-highlight">Card Payment Preview:</p>
                      <p className="text-xs text-ink-muted">
                        Invoice Amount: ${parseFloat(newInvoice.amount || 0).toFixed(2)}<br/>
                        Processing Fee ({newInvoice.creditCardFeePercent}%): +${(parseFloat(newInvoice.amount || 0) * parseFloat(newInvoice.creditCardFeePercent || 0) / 100).toFixed(2)}<br/>
                        {parseFloat(newInvoice.creditCardDiscount || 0) > 0 && (
                          <>Discount: -${parseFloat(newInvoice.creditCardDiscount || 0).toFixed(2)}<br/></>
                        )}
                        <strong>Total if Paid by Card: ${(
                          parseFloat(newInvoice.amount || 0) + 
                          (parseFloat(newInvoice.amount || 0) * parseFloat(newInvoice.creditCardFeePercent || 0) / 100) - 
                          parseFloat(newInvoice.creditCardDiscount || 0)
                        ).toFixed(2)}</strong>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={creatingInvoice}
              className="w-full bg-brand-highlight hover:bg-brand-highlight/90 text-white rounded-xl"
              data-testid="create-invoice-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {creatingInvoice 
                ? (language === 'es' ? 'Creando...' : 'Creating...') 
                : (language === 'es' ? 'Crear y Enviar Factura' : 'Create & Send Invoice')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Invoice History */}
      <Card className="bg-white border border-line shadow-sm">
        <CardHeader className="pb-2 border-b border-line">
          <CardTitle className="text-xl font-semibold text-ink-main flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-brand-primary" />
              {language === 'es' ? 'Historial de Facturas' : 'Invoice Summary'}
            </span>
            <div className="flex items-center gap-2">
              {/* Privacy Toggle - Hide Amounts */}
              <Button
                onClick={() => setHideAmounts(!hideAmounts)}
                variant="ghost"
                size="sm"
                className={`${hideAmounts ? 'text-red-600 bg-red-50' : 'text-ink-muted'}`}
                title={hideAmounts ? "Show Amounts" : "Hide Amounts (Privacy Mode)"}
              >
                {hideAmounts ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              {/* Tax Report PDF */}
              <Button
                onClick={handleExportTaxReportPDF}
                variant="outline"
                size="sm"
                className="text-brand-highlight hover:text-brand-highlight/80 border-brand-highlight/30 hover:bg-brand-highlight/10 text-xs"
                title="Generate Tax Report PDF"
                disabled={dateFilteredInvoices.length === 0}
              >
                <FileText className="w-3 h-3 mr-1" />
                Tax Report
              </Button>
              <Button
                onClick={handleExportAll}
                variant="outline"
                size="sm"
                className="text-brand-primary hover:text-brand-primary/80 text-xs"
                title="Export All to CSV"
              >
                <Download className="w-3 h-3 mr-1" />
                Export CSV
              </Button>
              <Button
                onClick={fetchInvoices}
                variant="ghost"
                size="sm"
                className="text-brand-primary hover:text-brand-primary/80"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardTitle>
          
          {/* Date Range Selector */}
          {!showArchived && (
            <div className="mt-3 p-3 bg-brand-primary/10 border border-brand-primary/30 rounded-xl">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CalendarRange className="w-4 h-4 text-brand-primary" />
                  <span className="text-sm font-medium text-brand-primary">Date Range:</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="text-xs border-brand-primary/30 bg-white">
                        <CalendarDays className="w-3 h-3 mr-1" />
                        {format(invoiceDateRange.from, 'MMM d, yyyy')} - {format(invoiceDateRange.to, 'MMM d, yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <div className="p-3 border-b">
                        <p className="text-sm font-medium text-ink-main mb-2">Select Date Range</p>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => {
                              setInvoiceDateRange({
                                from: startOfMonth(new Date()),
                                to: endOfMonth(new Date())
                              });
                            }}
                          >
                            This Month
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => {
                              const lastMonth = new Date();
                              lastMonth.setMonth(lastMonth.getMonth() - 1);
                              setInvoiceDateRange({
                                from: startOfMonth(lastMonth),
                                to: endOfMonth(lastMonth)
                              });
                            }}
                          >
                            Last Month
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => {
                              const threeMonthsAgo = new Date();
                              threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                              setInvoiceDateRange({
                                from: startOfMonth(threeMonthsAgo),
                                to: endOfMonth(new Date())
                              });
                            }}
                          >
                            Last 3 Months
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => {
                              setInvoiceDateRange({
                                from: new Date(new Date().getFullYear(), 0, 1),
                                to: new Date()
                              });
                            }}
                          >
                            Year to Date
                          </Button>
                        </div>
                      </div>
                      <div className="p-3">
                        {/* Date Input Fields */}
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <Label className="text-xs font-medium text-ink-main mb-1 block">Start Date</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              className={`w-full justify-start text-left font-normal ${activeDateField === 'from' ? 'ring-2 ring-brand-primary' : ''}`}
                              onClick={() => setActiveDateField('from')}
                            >
                              <CalendarDays className="w-3 h-3 mr-2" />
                              {format(invoiceDateRange.from, 'MMM d, yyyy')}
                            </Button>
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-ink-main mb-1 block">End Date</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              className={`w-full justify-start text-left font-normal ${activeDateField === 'to' ? 'ring-2 ring-brand-primary' : ''}`}
                              onClick={() => setActiveDateField('to')}
                            >
                              <CalendarDays className="w-3 h-3 mr-2" />
                              {format(invoiceDateRange.to, 'MMM d, yyyy')}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Instruction */}
                        <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 p-2 rounded mb-3">
                          <strong>Active:</strong> {activeDateField === 'from' ? 'Start Date' : 'End Date'} - Click a date below to set it
                        </div>

                        {/* Single Calendar */}
                        <Calendar
                          mode="single"
                          selected={activeDateField === 'from' ? invoiceDateRange.from : invoiceDateRange.to}
                          onSelect={(date) => {
                            if (date) {
                              if (activeDateField === 'from') {
                                setInvoiceDateRange(prev => ({ ...prev, from: date }));
                              } else {
                                setInvoiceDateRange(prev => ({ ...prev, to: date }));
                              }
                            }
                          }}
                          className="rounded-md border mx-auto"
                        />
                        
                        {/* Validation Warning */}
                        {invoiceDateRange.to < invoiceDateRange.from && (
                          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 p-2 rounded mt-3">
                            ⚠️ End date is before start date
                          </div>
                        )}
                      </div>
                      <div className="p-3 border-t bg-surface-bg">
                        <Button 
                          size="sm" 
                          className="w-full"
                          onClick={() => setShowDatePicker(false)}
                        >
                          Apply Date Range
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <span className="text-xs text-brand-primary">
                    ({dateFilteredInvoices.length} invoices in range)
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Summary Cards - Total Paid & Outstanding */}
          {!showArchived && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-600 font-medium">Total Paid</p>
                    <p className="text-xl font-bold text-green-700">
                      {hideAmounts ? '••••••' : `$${totalPaid.toFixed(2)}`}
                    </p>
                    <p className="text-xs text-green-500">{paidInvoices.length} invoice(s)</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      onClick={() => handlePrintInvoiceReport("paid")}
                      variant="ghost"
                      size="sm"
                      className="text-green-600 hover:bg-green-100 p-1"
                      title="Print Paid Report"
                      disabled={paidInvoices.length === 0}
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleExportInvoiceReportPDF("paid")}
                      variant="ghost"
                      size="sm"
                      className="text-green-600 hover:bg-green-100 p-1"
                      title="Download PDF Report"
                      disabled={paidInvoices.length === 0}
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-600 font-medium">Total Outstanding</p>
                    <p className="text-xl font-bold text-amber-700">
                      {hideAmounts ? '••••••' : `$${totalOutstanding.toFixed(2)}`}
                    </p>
                    <p className="text-xs text-amber-500">{unpaidInvoices.length} invoice(s)</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      onClick={() => handlePrintInvoiceReport("unpaid")}
                      variant="ghost"
                      size="sm"
                      className="text-amber-600 hover:bg-amber-100 p-1"
                      title="Print Unpaid Report"
                      disabled={unpaidInvoices.length === 0}
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleExportInvoiceReportPDF("unpaid")}
                      variant="ghost"
                      size="sm"
                      className="text-amber-600 hover:bg-amber-100 p-1"
                      title="Download PDF Report"
                      disabled={unpaidInvoices.length === 0}
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* View Tabs - All / Paid / Unpaid / Archive */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {!showArchived ? (
              <>
                <Button
                  onClick={() => setInvoiceView("all")}
                  variant={invoiceView === "all" ? "default" : "outline"}
                  size="sm"
                  className={`text-xs ${invoiceView === "all" ? 'bg-ink-main text-white' : 'border-line'}`}
                >
                  All ({dateFilteredInvoices.length})
                </Button>
                <Button
                  onClick={() => setInvoiceView("paid")}
                  variant={invoiceView === "paid" ? "default" : "outline"}
                  size="sm"
                  className={`text-xs ${invoiceView === "paid" ? 'bg-green-600 text-white' : 'border-green-300 text-green-700'}`}
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Paid ({paidInvoices.length})
                </Button>
                <Button
                  onClick={() => setInvoiceView("unpaid")}
                  variant={invoiceView === "unpaid" ? "default" : "outline"}
                  size="sm"
                  className={`text-xs ${invoiceView === "unpaid" ? 'bg-amber-500 text-white' : 'border-amber-300 text-amber-700'}`}
                >
                  <Receipt className="w-3 h-3 mr-1" />
                  Unpaid ({unpaidInvoices.length})
                </Button>
              </>
            ) : null}
            <Button
              onClick={() => { setShowArchived(!showArchived); setInvoiceView("all"); }}
              variant="outline"
              size="sm"
              className={`text-xs ${showArchived ? 'bg-purple-100 border-purple-300' : 'border-purple-200 text-purple-600'}`}
            >
              <Archive className="w-3 h-3 mr-1" />
              {showArchived ? 'Back to Active' : `Archive (${archivedInvoices.length})`}
            </Button>
          </div>
          
          {/* Bulk Actions Toolbar */}
          {filteredInvoices.length > 0 && (
            <p className="text-xs text-ink-muted mt-3 flex items-center gap-1" data-testid="bounce-help-text">
              <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
              A red "Email Bounced" badge means the invoice email did not reach the recipient. See details under Settings → Email Activity.
            </p>
          )}
          {filteredInvoices.length > 0 && (
            <div className="flex items-center gap-2 mt-3 p-2 bg-surface-bg rounded-xl border border-line flex-wrap">
              <Button
                onClick={toggleSelectAll}
                variant="ghost"
                size="sm"
                className="text-xs"
              >
                {selectedInvoices.length === filteredInvoices.length 
                  ? <CheckSquare className="w-4 h-4 mr-1 text-green-600" />
                  : <Square className="w-4 h-4 mr-1" />
                }
                {selectedInvoices.length === filteredInvoices.length 
                  ? 'Deselect All' 
                  : 'Select All'
                }
              </Button>
              
              {selectedInvoices.length > 0 && (
                <>
                  <span className="text-xs text-ink-muted px-2 border-l">
                    {selectedInvoices.length} selected
                  </span>
                  
                  {!showArchived && (
                    <Button
                      onClick={handleBulkArchive}
                      variant="outline"
                      size="sm"
                      className="text-xs text-purple-600 border-purple-300 hover:bg-purple-50"
                    >
                      <Archive className="w-3 h-3 mr-1" />
                      Archive Selected
                    </Button>
                  )}
                  
                  <Button
                    onClick={handleExportSelected}
                    variant="outline"
                    size="sm"
                    className="text-xs text-brand-primary border-brand-primary/30 hover:bg-brand-primary/10"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Export Selected
                  </Button>
                  
                  {showArchived && (
                    <Button
                      onClick={handleBulkDelete}
                      variant="outline"
                      size="sm"
                      className="text-xs text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete Selected
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-4">
          {invoiceLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-8">
              <Receipt className="w-12 h-12 text-ink-muted/30 mx-auto mb-3" />
              <p className="text-ink-muted">
                {showArchived 
                  ? (language === 'es' ? 'No hay facturas archivadas' : 'No archived invoices')
                  : invoiceView === "paid"
                    ? (language === 'es' ? 'No hay facturas pagadas' : 'No paid invoices')
                    : invoiceView === "unpaid"
                      ? (language === 'es' ? 'No hay facturas pendientes' : 'No unpaid invoices')
                      : (language === 'es' ? 'No hay facturas activas' : 'No active invoices')
                }
              </p>
              {!showArchived && invoiceView === "all" && (
                <p className="text-xs text-ink-muted/70 mt-1">
                  {language === 'es' ? 'Crea tu primera factura arriba' : 'Create your first invoice above'}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {/* Filtered Invoices (All/Paid/Unpaid or Archived) */}
              {filteredInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className={`p-4 rounded-xl border ${showArchived ? 'bg-purple-50 border-purple-200' : invoice.paid ? 'bg-green-50 border-green-200' : 'bg-white border-amber-200'} ${selectedInvoices.includes(invoice.id) ? 'ring-2 ring-brand-primary' : ''}`}
                  data-testid={`invoice-item-${invoice.invoiceNumber}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Selection Checkbox */}
                    <div className="pt-1">
                      <Checkbox
                        checked={selectedInvoices.includes(invoice.id)}
                        onCheckedChange={() => toggleInvoiceSelection(invoice.id)}
                        className="border-line"
                      />
                    </div>
                    
                    <div className="flex-1">
                      {/* Invoice Header */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-ink-main">{invoice.invoiceNumber}</p>
                        {showArchived && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full flex items-center gap-1">
                            <Archive className="w-3 h-3" /> ARCHIVED
                          </span>
                        )}
                        {invoice.paid ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> PAID
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                            PENDING
                          </span>
                        )}
                        {invoice.emailBounced === true && (
                          <span
                            className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full flex items-center gap-1"
                            data-testid={`invoice-email-bounced-${invoice.invoiceNumber}`}
                          >
                            <AlertCircle className="w-3 h-3" /> EMAIL BOUNCED
                          </span>
                        )}
                      </div>
                      
                      {/* Customer Info */}
                      <div className="mt-2 p-2 bg-surface-bg rounded border border-line">
                        <p className="text-sm font-medium text-ink-main">
                          <span className="text-ink-muted">Sent to:</span> {invoice.customerName}
                        </p>
                        <p className="text-xs text-ink-muted">{invoice.customerEmail}</p>
                      </div>
                      
                      {/* Description - More Visible */}
                      <div className="mt-2 p-2 bg-brand-primary/5 rounded border border-brand-primary/20">
                        <p className="text-xs text-brand-primary font-medium mb-1">Description:</p>
                        <p className="text-sm text-ink-main">{invoice.description}</p>
                        {invoice.notes && (
                          <p className="text-xs text-ink-muted mt-1 italic">Note: {invoice.notes}</p>
                        )}
                      </div>
                      
                      {/* Amount */}
                      <p className="text-xl font-bold text-brand-highlight mt-2">${invoice.amount.toFixed(2)}</p>
                      
                      {/* Dates Section */}
                      <div className="mt-2 text-xs space-y-1 border-t border-line pt-2">
                        <p className="text-ink-muted flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          <span className="font-medium">Sent:</span> {new Date(invoice.createdAt).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {invoice.paid && invoice.paidDate && (
                          <p className="text-green-600 font-medium flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Paid: {invoice.paidDate}
                          </p>
                        )}
                        {invoice.viewedAt && (
                          <p className="text-blue-600 flex items-center gap-1" title={`First opened: ${new Date(invoice.viewedAt).toLocaleString()}`}>
                            <Eye className="w-3 h-3" />
                            Viewed {new Date(invoice.viewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {invoice.viewCount > 1 && <span className="text-blue-400 text-xs">({invoice.viewCount}x)</span>}
                          </p>
                        )}
                        {invoice.createdBy && (
                          <p className="text-ink-muted/70">
                            Created by: {invoice.createdBy}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-4">
                      {!invoice.paid && !showArchived && (
                        <div className="flex items-center gap-2 bg-green-50 p-2 rounded">
                          <Checkbox
                            id={`paid-${invoice.id}`}
                            onCheckedChange={(checked) => {
                              if (checked) handleMarkPaid(invoice.id);
                            }}
                            className="border-green-500 data-[state=checked]:bg-green-500"
                          />
                          <label htmlFor={`paid-${invoice.id}`} className="text-xs text-green-700 cursor-pointer font-medium">
                            {language === 'es' ? 'Marcar Pagado' : 'Mark Paid'}
                          </label>
                        </div>
                      )}
                      <div className="flex gap-1 justify-end flex-wrap">
                        {/* Print Button */}
                        <Button
                          onClick={() => handlePrintInvoice(invoice)}
                          variant="ghost"
                          size="sm"
                          className="text-ink-muted hover:text-ink-main hover:bg-surface-bg p-2"
                          title="Print Invoice"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        {/* PDF Export Button */}
                        <Button
                          onClick={() => handleExportInvoicePDF(invoice)}
                          variant="ghost"
                          size="sm"
                          className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 p-2"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {!invoice.paid && !showArchived && (
                          <Button
                            onClick={() => setResendTarget(invoice.id)}
                            variant="ghost"
                            size="sm"
                            className="text-brand-primary hover:text-brand-primary/80 hover:bg-brand-primary/10 p-2"
                            title="Resend Invoice"
                            data-testid={`resend-invoice-${invoice.invoiceNumber}`}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Archive/Restore Button */}
                        {showArchived ? (
                          <>
                            <Button
                              onClick={() => handleUnarchiveInvoice(invoice.id)}
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-800 hover:bg-green-50 p-2"
                              title="Restore from Archive"
                            >
                              <ArchiveRestore className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2"
                              title="Permanently Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            onClick={() => handleArchiveInvoice(invoice.id)}
                            variant="ghost"
                            size="sm"
                            className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 p-2"
                            title="Move to Archive"
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsTab;
